import { Appointment } from '../types';
import { AppointmentStatus } from '../types/workflow';

export const isAppointmentToday = (appointmentDate: string): boolean => {
  const today = new Date();
  const apptDate = new Date(appointmentDate);
  
  return (
    today.getFullYear() === apptDate.getFullYear() &&
    today.getMonth() === apptDate.getMonth() &&
    today.getDate() === apptDate.getDate()
  );
};

export const isAppointmentInPast = (appointmentDate: string): boolean => {
  const today = new Date();
  const apptDate = new Date(appointmentDate);
  
  // Reset time to start of day for accurate comparison
  today.setHours(0, 0, 0, 0);
  apptDate.setHours(0, 0, 0, 0);
  
  return apptDate.getTime() < today.getTime();
};

export const isAppointmentInFuture = (appointmentDate: string): boolean => {
  const today = new Date();
  const apptDate = new Date(appointmentDate);
  
  // Reset time to start of day for accurate comparison
  today.setHours(0, 0, 0, 0);
  apptDate.setHours(0, 0, 0, 0);
  
  return apptDate.getTime() > today.getTime();
};

export const canStartWorkflow = (appointment: Appointment): { canStart: boolean; reason?: string } => {
  // Check if appointment is confirmed or scheduled
  if (!['CONFIRMED', 'SCHEDULED'].includes(appointment.status)) {
    return {
      canStart: false,
      reason: `Cannot start workflow for ${appointment.status.toLowerCase()} appointment`
    };
  }

  // Check if appointment is in the past
  if (isAppointmentInPast(appointment.appointmentDate)) {
    return {
      canStart: false,
      reason: 'Cannot start workflow for past appointments'
    };
  }

  // Check if appointment is in the future
  if (isAppointmentInFuture(appointment.appointmentDate)) {
    return {
      canStart: false,
      reason: 'Cannot check-in for future appointments. Patient must arrive on appointment date.'
    };
  }

  // Check if appointment is today
  if (isAppointmentToday(appointment.appointmentDate)) {
    return { canStart: true };
  }

  return {
    canStart: false,
    reason: 'Appointment date validation failed'
  };
};

export const getWorkflowStatusFromAppointment = (appointment: Appointment): AppointmentStatus => {
  // Auto check-in logic for same-day appointments
  if (isAppointmentToday(appointment.appointmentDate) && 
      ['CONFIRMED', 'SCHEDULED'].includes(appointment.status)) {
    return 'CHECKED_IN';
  }

  // Map appointment status to workflow status
  switch (appointment.status) {
    case 'CONFIRMED':
    case 'SCHEDULED':
      return isAppointmentToday(appointment.appointmentDate) ? 'CHECKED_IN' : 'SCHEDULED';
    case 'IN_PROGRESS':
      return 'WITH_DOCTOR';
    case 'COMPLETED':
      return 'COMPLETED';
    case 'CANCELLED':
      return 'CANCELLED';
    default:
      return 'SCHEDULED';
  }
};

export const shouldAutoAdvanceToWaiting = (appointment: Appointment): boolean => {
  return isAppointmentToday(appointment.appointmentDate) && 
         ['CONFIRMED', 'SCHEDULED'].includes(appointment.status);
};

export const getAppointmentTimeStatus = (appointmentDate: string, startTime: string): {
  status: 'early' | 'on-time' | 'late' | 'future';
  message: string;
} => {
  const now = new Date();
  const apptDateTime = new Date(`${appointmentDate} ${startTime}`);
  
  if (isAppointmentInFuture(appointmentDate)) {
    return {
      status: 'future',
      message: `Appointment scheduled for ${new Date(appointmentDate).toLocaleDateString()}`
    };
  }

  const diffMinutes = Math.floor((now.getTime() - apptDateTime.getTime()) / (1000 * 60));

  if (diffMinutes < -15) {
    return {
      status: 'early',
      message: `Patient is ${Math.abs(diffMinutes)} minutes early`
    };
  } else if (diffMinutes >= -15 && diffMinutes <= 15) {
    return {
      status: 'on-time',
      message: 'Patient is on time'
    };
  } else {
    return {
      status: 'late',
      message: `Patient is ${diffMinutes} minutes late`
    };
  }
};

export const formatAppointmentDateTime = (appointmentDate: string, startTime: string): string => {
  const date = new Date(appointmentDate);
  return `${date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  })} at ${startTime}`;
};

/** Display name for who created the appointment: staff name, then staff email, username, or user email. */
export const getAppointmentCreatorLabel = (
  createdBy: Appointment['createdBy'] | null | undefined
): string | null => {
  if (!createdBy) return null;
  const staff = createdBy.staff;
  const fromStaffName =
    staff != null ? `${staff.firstName ?? ''} ${staff.lastName ?? ''}`.trim() : '';
  if (fromStaffName) return fromStaffName;
  const staffEmail = staff?.email?.trim();
  if (staffEmail) return staffEmail;
  const username = createdBy.username?.trim();
  if (username) return username;
  const userEmail =
    typeof createdBy.email === 'string' ? createdBy.email.trim() : '';
  if (userEmail) return userEmail;
  return null;
};